import { useEffect, useId, useMemo, useRef, useState } from 'react'

/**
 * Dependency-free SVG line chart for time series (same reasoning as Charts.tsx: no chart
 * library exists in console/package.json and one chart is not worth the bundle).
 * Every scale is guarded so an empty series, a single point or an all-zero range renders
 * a flat baseline instead of `NaN` coordinates -- React happily writes "NaN" into a path
 * attribute and the chart just vanishes with no error.
 */

export interface LinePoint {
  /** x in the series' own units (epoch ms for dates, an index for ordinal buckets). */
  x: number
  y: number
}

export interface LineSeries {
  key: string
  label: string
  color: string
  points: LinePoint[]
}

export interface LineChartProps {
  series: LineSeries[]
  height?: number
  yLabel?: string
  formatX?: (x: number) => string
  formatY?: (y: number) => string
  /** Required for screen readers: what this picture of lines is about. */
  ariaLabel: string
  /** Max x tick labels to draw (they are spaced evenly over the x extent). */
  xTicks?: number
}

const PAD = { top: 10, right: 12, bottom: 22, left: 36 }
/** Fallback before the first measurement (and in jsdom, which has no ResizeObserver). */
const DEFAULT_WIDTH = 640
/** Closest two x labels may sit, in px -- a 'DD Mon' label in 9px mono is ~42px wide. */
const MIN_LABEL_GAP = 64

/** Evenly spread candidate ticks, thinned so no two land closer than MIN_LABEL_GAP: real
 *  buckets are not evenly spaced in time (a gap of quiet days), so picking every n-th
 *  bucket by index put '20 Oct' and '21 Oct' on top of each other. */
export function pickXLabels(xs: readonly number[], sx: (x: number) => number, maxTicks: number): number[] {
  const n = Math.max(2, Math.min(maxTicks, xs.length))
  const candidates = xs.length <= n
    ? [...xs]
    : Array.from({ length: n }, (_, i) => xs[Math.round((i * (xs.length - 1)) / (n - 1))]!)
  const out: number[] = []
  for (const x of candidates) {
    const last = out.at(-1)
    if (last === undefined || sx(x) - sx(last) >= MIN_LABEL_GAP) out.push(x)
  }
  return out
}

function finite(n: number, fallback: number): number {
  return Number.isFinite(n) ? n : fallback
}

/** "Nice" y max: round the data max up to 1/2/5 × 10^k so gridlines land on round numbers. */
function niceMax(max: number): number {
  if (!(max > 0)) return 1
  const mag = 10 ** Math.floor(Math.log10(max))
  const norm = max / mag
  const step = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10
  return step * mag
}

export function LineChart({
  series, height = 180, yLabel, formatX, formatY, ariaLabel, xTicks = 6,
}: LineChartProps): JSX.Element {
  const clipId = useId()
  const [hover, setHover] = useState<{ sx: number; x: number } | null>(null)
  // The viewBox is the real pixel width, so 9px axis text renders at 9px. A fixed 640-wide
  // viewBox with preserveAspectRatio="none" stretched every label sideways on a wide card.
  const wrapRef = useRef<HTMLDivElement>(null)
  const [WIDTH, setWidth] = useState(DEFAULT_WIDTH)
  useEffect(() => {
    const el = wrapRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(([entry]) => {
      const w = Math.round(entry?.contentRect.width ?? 0)
      if (w > 0) setWidth(Math.max(240, w))
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const model = useMemo(() => {
    const pts = series.flatMap((s) => s.points).filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
    const xs = pts.map((p) => p.x)
    const ys = pts.map((p) => p.y)
    const xMin = xs.length ? Math.min(...xs) : 0
    const xMaxRaw = xs.length ? Math.max(...xs) : 1
    // A single x value has no extent: widen it by one unit either side so the point sits
    // mid-chart instead of dividing by zero.
    const xMax = xMaxRaw > xMin ? xMaxRaw : xMin + 1
    const yMax = niceMax(ys.length ? Math.max(0, ...ys) : 0)
    const innerW = WIDTH - PAD.left - PAD.right
    const innerH = height - PAD.top - PAD.bottom
    const sx = (x: number) => PAD.left + finite(((x - xMin) / (xMax - xMin)) * innerW, 0)
    const sy = (y: number) => PAD.top + innerH - finite((Math.max(0, y) / yMax) * innerH, 0)
    const xUnique = [...new Set(xs)].sort((a, b) => a - b)
    return { pts, xMin, xMax, yMax, innerW, innerH, sx, sy, xUnique }
  }, [series, height, WIDTH])

  const { xMin, xMax, yMax, innerW, innerH, sx, sy, xUnique, pts } = model
  const fmtX = formatX ?? ((x: number) => String(x))
  const fmtY = formatY ?? ((y: number) => y.toLocaleString('en-IN'))

  if (pts.length === 0) {
    return <p className="py-6 text-center text-[11px] text-slate-400">No data in this window.</p>
  }

  // Y gridlines at 0, ¼, ½, ¾, 1 of the nice max; x labels evenly over the real buckets.
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * yMax)
  const xLabelXs = pickXLabels(xUnique, sx, xTicks)
  // Labels at the very edges anchor inward so they are not clipped by the viewBox.
  const anchorFor = (x: number): 'start' | 'middle' | 'end' => {
    const px = sx(x)
    if (px < PAD.left + 24) return 'start'
    if (px > WIDTH - PAD.right - 24) return 'end'
    return 'middle'
  }

  // Nearest real x to the pointer, for the hover rule + readout.
  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    if (rect.width <= 0) return
    const px = ((e.clientX - rect.left) / rect.width) * WIDTH
    const x = xMin + ((px - PAD.left) / innerW) * (xMax - xMin)
    let best = xUnique[0]!
    for (const cand of xUnique) if (Math.abs(cand - x) < Math.abs(best - x)) best = cand
    setHover({ sx: sx(best), x: best })
  }

  return (
    <div ref={wrapRef}>
      <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        {series.map((s) => (
          <span key={s.key} className="flex items-center gap-1.5 text-[10px] text-slate-500">
            <span className="h-[3px] w-3 rounded-full" style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
        {yLabel ? <span className="ml-auto text-[10px] text-slate-400">{yLabel}</span> : null}
      </div>
      <svg
        viewBox={`0 0 ${WIDTH} ${height}`}
        role="img"
        aria-label={ariaLabel}
        className="block w-full"
        style={{ height }}
        preserveAspectRatio="none"
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          <clipPath id={clipId}>
            <rect x={PAD.left} y={PAD.top} width={innerW} height={innerH} />
          </clipPath>
        </defs>
        {yTicks.map((t) => (
          <g key={t}>
            <line x1={PAD.left} x2={WIDTH - PAD.right} y1={sy(t)} y2={sy(t)} stroke="#E2E8F0" strokeWidth={1} />
            <text x={PAD.left - 6} y={sy(t) + 3} textAnchor="end" fontSize={9} fill="#94A3B8" fontFamily="JetBrains Mono, ui-monospace, monospace">
              {fmtY(t)}
            </text>
          </g>
        ))}
        {xLabelXs.map((x) => (
          <text key={x} x={sx(x)} y={height - 6} textAnchor={anchorFor(x)} fontSize={9} fill="#94A3B8" fontFamily="JetBrains Mono, ui-monospace, monospace">
            {fmtX(x)}
          </text>
        ))}
        <g clipPath={`url(#${clipId})`}>
          {series.map((s) => {
            const sorted = [...s.points]
              .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
              .sort((a, b) => a.x - b.x)
            if (!sorted.length) return null
            const d = sorted.map((p, i) => `${i === 0 ? 'M' : 'L'}${sx(p.x).toFixed(1)} ${sy(p.y).toFixed(1)}`).join(' ')
            return (
              <g key={s.key}>
                <path d={d} fill="none" stroke={s.color} strokeWidth={1.75} strokeLinejoin="round" strokeLinecap="round" />
                {sorted.length <= 60 && sorted.map((p) => (
                  <circle key={p.x} cx={sx(p.x)} cy={sy(p.y)} r={2} fill={s.color}>
                    <title>{`${s.label}: ${fmtY(p.y)} at ${fmtX(p.x)}`}</title>
                  </circle>
                ))}
              </g>
            )
          })}
        </g>
        {hover ? (
          <line x1={hover.sx} x2={hover.sx} y1={PAD.top} y2={PAD.top + innerH} stroke="#94A3B8" strokeDasharray="2 3" strokeWidth={1} />
        ) : null}
      </svg>
      {hover ? (
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-slate-500" aria-live="polite">
          <span className="font-medium text-slate-700">{fmtX(hover.x)}</span>
          {series.map((s) => {
            const p = s.points.find((q) => q.x === hover.x)
            return (
              <span key={s.key} className="flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.color }} />
                {s.label} <span className="font-mono tabular-nums text-slate-700">{p ? fmtY(p.y) : '—'}</span>
              </span>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
